/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Brand Voice DTOs & Validation Schemas (OpenAPI 3.1 Compatible)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Standard brand voice tones supported by default.
 */
export const BrandVoiceToneEnum = z
  .enum([
    'professional',
    'authoritative',
    'friendly',
    'casual',
    'witty',
    'luxury',
    'persuasive',
    'educational',
    'empathetic',
    'bold',
  ])
  .openapi({
    description: 'Tone of voice personality profile',
    example: 'professional',
  });

export type BrandVoiceTone = z.infer<typeof BrandVoiceToneEnum>;

/**
 * Individual terminology replacement or preference rule.
 */
export const TerminologyRuleSchema = z
  .object({
    term: z.string().min(1).openapi({ description: 'The word or phrase to target', example: 'Cloud-hosted' }),
    replace: z.string().optional().openapi({ description: 'Preferred replacement term', example: 'Edge-native' }),
    instruction: z
      .string()
      .optional()
      .openapi({ description: 'Custom directive for how to use this term', example: 'Always use Edge-native instead of Cloud-hosted' }),
  })
  .openapi('TerminologyRule');

export type TerminologyRule = z.infer<typeof TerminologyRuleSchema>;

/**
 * Complete Brand Voice Profile stored inside Workspace Settings.
 */
export const BrandVoiceProfileSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique Brand Voice UUID identifier', example: 'bv_8f3a12bc90de45f1' }),
    workspaceId: z.string().uuid().openapi({ description: 'Owning Workspace UUID', example: '123e4567-e89b-12d3-a456-426614174000' }),
    name: z.string().min(1).max(100).openapi({ description: 'Profile display name', example: 'Official Corporate Voice' }),
    brandName: z.string().min(1).max(100).openapi({ description: 'Company or Product brand name', example: 'Sophia Platform' }),
    companyBio: z
      .string()
      .max(1000)
      .optional()
      .nullable()
      .openapi({ description: 'Brief introduction to company mission and values', example: 'Leading high-performance Cloudflare Edge-Native AI platform.' }),
    targetAudience: z
      .string()
      .max(500)
      .optional()
      .nullable()
      .openapi({ description: 'Target audience persona and key demographics', example: 'CTOs, Software Architects and Content Marketing Teams.' }),
    defaultTone: z
      .string()
      .min(1)
      .default('professional')
      .openapi({ description: 'Primary tone of voice', example: 'professional' }),
    bannedWords: z
      .array(z.string().min(1))
      .default([])
      .openapi({
        description: 'Forbidden words or buzzwords that must never appear in generated text',
        example: ['revolutionary', 'game-changing', 'synergy'],
      }),
    preferredTerminology: z
      .array(TerminologyRuleSchema)
      .default([])
      .openapi({ description: 'Glossary rules for specialized brand vocabulary' }),
    customInstructions: z
      .string()
      .max(2000)
      .optional()
      .nullable()
      .openapi({ description: 'Additional free-form editorial rules and style constraints', example: 'Keep paragraphs under 3 sentences. Use active voice.' }),
    isDefault: z.boolean().default(false).openapi({ description: 'Whether this is the workspace default brand voice', example: true }),
    createdAt: z.string().openapi({ description: 'ISO 8601 creation timestamp', example: '2026-10-19T20:00:00.000Z' }),
    updatedAt: z.string().openapi({ description: 'ISO 8601 last update timestamp', example: '2026-10-19T20:00:00.000Z' }),
  })
  .openapi('BrandVoiceProfile');

export type BrandVoiceProfile = z.infer<typeof BrandVoiceProfileSchema>;

/**
 * Request payload for creating a new Brand Voice profile.
 */
export const CreateBrandVoiceSchema = z
  .object({
    name: z.string().min(2, 'Name must be at least 2 characters').max(100).openapi({ description: 'Voice profile name', example: 'B2B Thought Leadership' }),
    brandName: z.string().min(2, 'Brand name must be at least 2 characters').max(100).openapi({ description: 'Brand name', example: 'Sophia Platform' }),
    companyBio: z.string().max(1000).optional().openapi({ description: 'Company biography', example: 'Edge-native Cloudflare AI SaaS platform.' }),
    targetAudience: z.string().max(500).optional().openapi({ description: 'Audience persona', example: 'DevOps engineers and SaaS founders.' }),
    defaultTone: z.string().min(1).default('professional').openapi({ description: 'Primary tone', example: 'authoritative' }),
    bannedWords: z.array(z.string().min(1)).default([]).openapi({ description: 'Forbidden words list', example: ['delve', 'synergy', 'tapestry'] }),
    preferredTerminology: z.array(TerminologyRuleSchema).default([]).openapi({ description: 'Preferred glossary rules' }),
    customInstructions: z.string().max(2000).optional().openapi({ description: 'Custom writing instructions', example: 'Use technical rigor and concise syntax.' }),
    isDefault: z.boolean().default(false).openapi({ description: 'Set as workspace default voice', example: true }),
  })
  .openapi('CreateBrandVoice');

export type CreateBrandVoiceDto = z.infer<typeof CreateBrandVoiceSchema>;

/**
 * Request payload for updating an existing Brand Voice profile.
 */
export const UpdateBrandVoiceSchema = CreateBrandVoiceSchema.partial().openapi('UpdateBrandVoice');

export type UpdateBrandVoiceDto = z.infer<typeof UpdateBrandVoiceSchema>;

/**
 * Options for injecting Brand Voice into LLM Prompts.
 */
export interface BrandVoiceInjectionOptions {
  toneOverride?: string;
  customBannedWords?: string[];
  audienceOverride?: string;
}
